import { OptionStrategies } from '@src/components/MultilegCombo/enums';
import type { SelectItem } from '@talos/kyoko';
import { OptionTypeEnum, toBigWithDefault, type StringSelectItem } from '@talos/kyoko';
import type { OMSReferenceDataState } from '../../types';
import { OPTION_SIDES, Option } from './Option';
import { OptionStrategy, type OptionStrategyData } from './OptionStrategy';

/*
 * Strategy containing both a call and a put with same strike prices and same expiration
 * Useful if you think there will be a price movement on either direction
 * Similar to Straddle but more expensive premium to pay since you can earn money with a smaller price movement
 */
export class Straddle extends OptionStrategy {
  public static createFromBlank(referenceData: OMSReferenceDataState, partial?: Partial<OptionStrategyData>) {
    const data: OptionStrategyData = {
      name: OptionStrategies.Straddle,
      legs: [Option.createFromBlank(referenceData), Option.createFromBlank(referenceData).updateType(OPTION_SIDES[1])],
      initiatingLegs: [true, false],
      ratios: ['1', '1'],
      haircutRatios: ['0', '0'],
      ...partial,
    };

    return new Straddle(referenceData, data);
  }

  public updateExpiry = (expiry: StringSelectItem) => {
    const newData = {
      legs: [this._data.legs[0].updateExpiry(expiry), this._data.legs[1].updateExpiry(expiry)],
    };

    return this.create(newData);
  };

  public updateStrike = (strike: StringSelectItem, legIndex: number) => {
    const newData = {
      legs: [this._data.legs[0].updateStrike(strike), this._data.legs[1].updateStrike(strike)],
    };
    return this.create(newData);
  };

  public updateType = (type: SelectItem<OptionTypeEnum>, legIndex: number) => {
    const oppositeType = type.value === OptionTypeEnum.Call ? OPTION_SIDES[1] : OPTION_SIDES[0];

    const newData = {
      legs: [
        this._data.legs[0].updateType(legIndex === 0 ? type : oppositeType),
        this._data.legs[1].updateType(legIndex === 1 ? type : oppositeType),
      ],
    };
    return this.create(newData);
  };

  public updateRatio = (ratio: string, legIndex: number) => {
    const isRatioNegative = toBigWithDefault(ratio, 0)?.lt(0);

    if (!ratio || ratio === '0' || ratio === '-' || isRatioNegative) {
      // ratio must be a non-zero positive number
      return this.create(this.data);
    }

    const copy = this._data.ratios.slice();
    copy[legIndex] = ratio;

    const newData = {
      ratios: copy,
    };

    return this.create(newData);
  };

  public getPrettyName(): string {
    // todo when supported
    return '';
  }

  protected create(newData: Partial<OptionStrategyData>) {
    const data = {
      ...this._data,
      ...newData,
    };
    return new Straddle(this._referenceData, data);
  }
}
